import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Column, theme } from "uikit";

import { Executor } from "../../../../../services";
import { StyledRow, Colors } from "../../../../common";
import { getExecutorStatuses } from "../../constants";

const emptyCounts = {
	total: 0,
	online: 0,
	offline: 0,
	working: 0,
	notWorking: 0,
	closed: 0,
	available: 0,
	onOrder: 0,
	ownOrder: 0,
	busy: 0,
	dinner: 0,
	home: 0,
};

const countExecutors = (executors: Executor.Model[]) => {
	const counts = { ...emptyCounts };
	counts.total = executors.length;
	for (let i = 0; i < executors.length; i += 1) {
		const item = executors[i];
		if (item.online) counts.online += 1;
		else counts.offline += 1;
		if (item.isWorking) counts.working += 1;
		else counts.notWorking += 1;
		if (item.status === "closed") counts.closed += 1;
		if (item.status === "available") counts.available += 1;
		if (item.status === "on_order") counts.onOrder += 1;
		if (item.status === "busy") counts.busy += 1;
		if (item.status === "dinner") counts.dinner += 1;
		if (item.status === "home") counts.home += 1;
		if (
			item?.executorToOrders?.find(
				(order) => order.source === "executor" && !order.closedAt,
			)
		) {
			counts.ownOrder += 1;
		}
	}
	return counts;
};

const TableFooter: React.FC<TableFooter.Props> = ({ executors }) => {
	const { t } = useTranslation();
	const EXECUTOR_STATUSES = useMemo(() => getExecutorStatuses(t), [t]);
	const counts = useMemo(() => countExecutors(executors), [executors]);

	return (
		<StyledRow
			bgC={Colors.BACKGROUND_GRAY_2}
			b={{
				top: `1.5px solid ${theme.colors.button_secondary}`,
				bottom: `1.5px solid ${Colors.BORDER_GRAY}`,
			}}
		>
			<Column
				gaps="3px*"
				style={{
					borderRight: `1.5px solid ${theme.colors.button_secondary}`,
					paddingInline: "8px",
					paddingBlock: "4px",
				}}
			>
				<span className="totalRow">{`${t(`total`, "Total")}: ${
					counts.total
				}`}</span>
				<span className="totalRow">{`${t(
					`orderPage.executors.total_online`,
					"In the app",
				)}: ${counts.online}`}</span>
				<span className="totalRow">{`${t(
					`orderPage.executors.total_offline`,
					"Not in the app",
				)}: ${counts.offline}`}</span>
			</Column>
			<Column
				gaps="3px*"
				style={{
					borderRight: `1.5px solid ${theme.colors.button_secondary}`,
					paddingInline: "8px",
					paddingBlock: "4px",
				}}
			>
				<span className="totalRow">{`${t(
					`orderPage.executors.opened_schedule`,
					"Opened schedule",
				)}: ${counts.working}`}</span>
				<span className="totalRow">{`${t(
					`orderPage.executors.closed_schedule`,
					"Closed schedule",
				)}: ${counts.notWorking}`}</span>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.closed.label}: `}
					{counts.closed}
				</span>
			</Column>
			<Column
				gaps="3px*"
				style={{
					borderRight: `1.5px solid ${theme.colors.button_secondary}`,
					paddingInline: "8px",
					paddingBlock: "4px",
				}}
			>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.available.label}: `}
					{counts.available}
				</span>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.on_order.label}: `}
					{counts.onOrder}
				</span>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.ownOrder.label}: `}
					{counts.ownOrder}
				</span>
			</Column>
			<Column
				gaps="3px*"
				style={{
					paddingInline: "8px",
					paddingBlock: "4px",
				}}
			>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.busy.label}: `}
					{counts.busy}
				</span>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.dinner.label}: `}
					{counts.dinner}
				</span>
				<span className="totalRow">
					{`${EXECUTOR_STATUSES.home.label}: `}
					{counts.home}
				</span>
			</Column>
		</StyledRow>
	);
};

declare namespace TableFooter {
	interface Props {
		executors: Executor.Model[];
	}
}

export default React.memo(TableFooter);
